import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import "../../../css/createSourceModal.css";
import SwalAlertComp from "../../SwalAlert";
import LoginButton from "../../molecules/LoginButton";
import { ClipLoader } from "react-spinners";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import {
    getBigqueryLeadAuthUrl,
    bigqueryLeadAuthorize,
    getBigqueryLeadTables,
} from "../../../helpers/leads/bigquery/bigqueryLead";
import { Box, Button } from "@mui/material";

const BigQueryLeadDestination = ({
    onSuccess,
    isButtonLoader,
    setIsButtonLoader,
    setBigQueryData,
    onAuthSuccess,
}) => {
    const language = useSelector((state) => state.languagesState.languages);

    // Form fields
    const [name, setName] = useState("");

    // Auth flow states
    const [authUrl, setAuthUrl] = useState("");
    const [progressStep, setProgressStep] = useState(0);

    // BigQuery selection states
    const [installId, setInstallId] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [datasets, setDatasets] = useState([]);
    const [selectedDataset, setSelectedDataset] = useState(null);
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);

    // Loading state
    const [isLoading, setIsLoading] = useState(false);

    // Fetch auth URL on component mount
    useEffect(() => {
        const fetchAuthUrl = async () => {
            setIsLoading(true);
            const response = await getBigqueryLeadAuthUrl();
            if (response?.status) {
                setAuthUrl(response?.data?.auth_url);
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message ?? "Failed to fetch auth URL",
                    icon: "error",
                });
            }
            setIsLoading(false);
        };
        fetchAuthUrl();
    }, []);

    const isRun = useRef(false);

    // Handle OAuth success - authorize with code
    const handleAuthSuccess = async (data) => {
        if (isRun.current) return;
        if (data.code && !isRun.current) isRun.current = true;
        setIsLoading(true);
        if (setIsButtonLoader) {
            setIsButtonLoader(true);
        }

        const response = await bigqueryLeadAuthorize({ code: data.code });

        if (response?.status) {
            if (onAuthSuccess) {
                onAuthSuccess(response?.data?.data);
            }
            setInstallId(response?.data?.install_id);
            setProjects(response?.data?.projects || []);
            setProgressStep(1);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "Authorization failed",
                icon: "error",
            });
        }

        setIsLoading(false);
        if (setIsButtonLoader) {
            setIsButtonLoader(false);
        }
    };

    // Handle name input change
    const handleNameChange = (e) => {
        setName(e.target.value);
    };

    // Handle project selection
    const handleProjectChange = (event, selectedOpt) => {
        if (selectedOpt) {
            setSelectedProject(selectedOpt);
            const projectDatasets =
                projects.find((p) => p.project_id === selectedOpt.project_id)
                    ?.datasets || [];
            setDatasets(
                projectDatasets.map((ds) => ({ value: ds, label: ds })),
            );
            // Reset dependent selections
            setSelectedDataset(null);
            setTables([]);
            setSelectedTable(null);
        } else {
            // Clear all selections
            setSelectedProject(null);
            setDatasets([]);
            setSelectedDataset(null);
            setTables([]);
            setSelectedTable(null);
        }
    };

    // Handle dataset selection
    const handleDatasetChange = async (event, selectedOpt) => {
        if (selectedOpt?.value) {
            setSelectedDataset(selectedOpt.value);
            setSelectedTable(null);

            // Fetch tables for selected dataset
            setIsLoading(true);
            const response = await getBigqueryLeadTables(
                selectedProject.project_id,
                selectedOpt.value,
                installId,
            );

            if (response?.status) {
                setTables(
                    response?.data?.tables?.map((t) => ({
                        value: t,
                        label: t,
                    })) || [],
                );
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message ?? "Failed to fetch tables",
                    icon: "error",
                });
            }
            setIsLoading(false);
        } else {
            // Clear selection
            setSelectedDataset(null);
            setTables([]);
            setSelectedTable(null);
        }
    };

    // Handle table selection
    const handleTableChange = (event, selectedOpt) => {
        if (selectedOpt?.value) {
            setSelectedTable(selectedOpt.value);
        } else {
            setSelectedTable(null);
        }
    };

    useEffect(() => {
        if (setBigQueryData) {
            setBigQueryData({
                name: name,
                install_id: installId,
                project: selectedProject?.project_id,
                dataset: selectedDataset,
                table: selectedTable,
            });
        }
    }, [
        name,
        installId,
        selectedProject,
        selectedDataset,
        selectedTable,
        setBigQueryData,
    ]);
    return (
        <Box style={{ marginTop: "10px" }}>
            {progressStep === 0 && (
                <div>
                    {/* Name Input */}
                    <FormControl className="mb-3" style={{ width: "100%" }}>
                        <TextField
                            name="name"
                            label={language?.source_modal?.name ?? "Name"}
                            variant="outlined"
                            onChange={handleNameChange}
                            placeholder={
                                language?.source_modal?.please_write_name ??
                                "Please Write Name"
                            }
                            value={name}
                            required
                        />
                    </FormControl>

                    {/* Login Button */}
                    <div className="position-relative overflow-hidden z-1">
                        {(isLoading || isButtonLoader) && (
                            <div className="circle-loader">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}
                        <LoginButton
                            url={authUrl}
                            code="bigquery"
                            paramNames={["code"]}
                            onSuccess={handleAuthSuccess}
                            logoSrc="/assets/images/bigquery.png"
                            buttonText={
                                language?.buttons?.connect_with_bigquery ??
                                "Connect with Google BigQuery"
                            }
                            disabled={!name || isLoading}
                            variant="contained"
                            color="primary"
                            logoWidth={30}
                        />
                    </div>
                </div>
            )}

            {progressStep === 1 && (
                <div>
                    {/* Project Selection */}
                    <div className="mb-3">
                        <FormControl fullWidth>
                            <Autocomplete
                                name="project"
                                onChange={handleProjectChange}
                                value={selectedProject}
                                options={projects}
                                getOptionLabel={(option) =>
                                    option.project_name || ""
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={
                                            language?.destination_modal
                                                ?.project ?? "Project"
                                        }
                                        placeholder="Search for a project..."
                                        variant="outlined"
                                    />
                                )}
                            />
                        </FormControl>
                    </div>

                    {/* Dataset Selection */}
                    {selectedProject && (
                        <div className="mb-3">
                            <FormControl fullWidth>
                                <Autocomplete
                                    name="dataset"
                                    onChange={handleDatasetChange}
                                    value={
                                        datasets.find(
                                            (d) => d.value === selectedDataset,
                                        ) || null
                                    }
                                    options={datasets}
                                    getOptionLabel={(option) =>
                                        option.label || ""
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                language?.destination_modal
                                                    ?.dataset ?? "Dataset"
                                            }
                                            placeholder="Search for a dataset..."
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </div>
                    )}

                    {/* Table Selection */}
                    {selectedDataset && tables.length > 0 && (
                        <div className="mb-3">
                            <FormControl fullWidth>
                                <Autocomplete
                                    name="table"
                                    onChange={handleTableChange}
                                    value={
                                        tables.find(
                                            (t) => t.value === selectedTable,
                                        ) || null
                                    }
                                    options={tables}
                                    getOptionLabel={(option) =>
                                        option.label || ""
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                language?.destination_modal
                                                    ?.table ?? "Table"
                                            }
                                            placeholder="Search for a table..."
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </div>
                    )}

                    {/* Loading indicator for fetching tables */}
                    {selectedDataset && isLoading && tables.length === 0 && (
                        <div className="text-center mt-3">
                            <ClipLoader color={"#000"} size={25} />
                            <p className="mt-2">Loading tables...</p>
                        </div>
                    )}

                    {/* Empty state placeholder when no tables found */}
                    {selectedDataset && !isLoading && tables.length === 0 && (
                        <div
                            className="text-center mt-4 p-4"
                            style={{
                                backgroundColor: "#f8f9fa",
                                borderRadius: "8px",
                                border: "1px dashed #dee2e6",
                            }}
                        >
                            <div style={{ fontSize: "48px", opacity: 0.3 }}>
                                📊
                            </div>
                            <h5
                                className="mt-3"
                                style={{ color: "#6c757d", fontWeight: 600 }}
                            >
                                {language?.destination_modal?.no_tables_found ??
                                    "No Tables Found"}
                            </h5>
                            <p
                                className="mb-0"
                                style={{ color: "#6c757d", fontSize: "14px" }}
                            >
                                {language?.destination_modal
                                    ?.no_tables_in_dataset ??
                                    "This dataset doesn't contain any tables yet."}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </Box>
    );
};

export default BigQueryLeadDestination;
